import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin, Select } from 'antd'
import { useState } from 'react'

import { useIdentityServiceGetProfilesListEndpointKey } from 'src/api/queries'
import { GetProfilesListResponseItem, IdentityService } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  legalEntity?: string
  /** Narrows the list to the users holding these permissions, e.g. only the
   *  ones that count as doctors. */
  permissionKeys?: string[]
  /** Pre-loaded options (e.g. the currently selected user's name) so the
   *  select can show a label before the list loads or when the selected user
   *  isn't part of the loaded page. */
  initialOptions?: { value: string; label: string }[]
} & SelectProps

export const UsersInfinitySelect = ({
  legalEntity,
  permissionKeys,
  initialOptions,
  ...props
}: Props) => {
  const { t } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useIdentityServiceGetProfilesListEndpointKey,
      debouncedSearch,
      legalEntity,
      permissionKeys
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await IdentityService.getProfilesListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        legalEntity,
        undefined,
        undefined,
        undefined,
        permissionKeys
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetProfilesListResponseItem[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const listOptions =
    data?.pages.flatMap(
      (page) =>
        page.data?.map((item: GetProfilesListResponseItem) => ({
          value: item.id,
          label: `${item.lastName} ${item.firstName}`
        })) || []
    ) ?? []

  const initialValues = new Set(initialOptions?.map((o) => o.value) ?? [])
  const listOptionsFiltered = listOptions.filter(
    (o) => !initialValues.has(String(o?.value))
  )
  const userOptions = [...(initialOptions ?? []), ...listOptionsFiltered]

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={userOptions}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      placeholder={t('common.user')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
